import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const FinishesTab = ({ finishes, onAdd, onEdit, onDelete }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    shineLevel: 50,
    durability: '',
    usageCount: 0
  });

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        description: '',
        shineLevel: 50,
        durability: '',
        usageCount: 0
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (editingItem) {
      onEdit({ ...formData, id: editingItem?.id });
    } else {
      onAdd({ ...formData, id: Date.now() });
    }
    handleCloseModal();
  };

  const handleDelete = (item) => {
    if (window.confirm(`Delete "${item?.name}"? This will affect ${item?.usageCount} companies.`)) {
      onDelete(item?.id);
    }
  };

  const getShineIcon = (level) => {
    if (level >= 80) return 'Sparkles';
    if (level >= 40) return 'Circle';
    return 'CircleDot';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Finishes</h2>
          <p className="text-sm text-text-secondary mt-1">
            Manage default finish options (glossy, matte, eggshell)
          </p>
        </div>
        <Button
          variant="default"
          onClick={() => handleOpenModal()}
          iconName="Plus"
          iconPosition="left"
          iconSize={16}
        >
          Add Finish
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {finishes?.map((finish) => (
          <div
            key={finish?.id}
            className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow duration-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={getShineIcon(finish?.shineLevel)} size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">{finish?.name}</h3>
                  <p className="text-xs text-text-secondary">Shine: {finish?.shineLevel}%</p>
                </div>
              </div>
            </div>

            <p className="text-sm text-text-secondary mb-4 line-clamp-3">
              {finish?.description}
            </p>

            {finish?.durability && (
              <div className="flex items-center gap-2 text-xs text-text-secondary mb-4">
                <Icon name="Shield" size={14} />
                <span>{finish?.durability}</span>
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-text-secondary mb-4 pb-4 border-b border-border">
              <Icon name="Building2" size={14} />
              <span>Used by {finish?.usageCount} companies</span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenModal(finish)}
                iconName="Edit2"
                iconPosition="left"
                iconSize={14}
                className="flex-1"
              >
                Edit
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDelete(finish)}
                iconName="Trash2"
                iconSize={14}
              />
            </div>
          </div>
        ))}
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text-primary">
                {editingItem ? 'Edit Finish' : 'Add Finish'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-text-secondary hover:text-text-primary transition-colors duration-150"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <Input
                label="Finish Name"
                type="text"
                placeholder="e.g., High Gloss"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                required
              />

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Description
                </label>
                <textarea
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-ring"
                  rows="3"
                  placeholder="Describe this finish type..."
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Shine Level: {formData?.shineLevel}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={formData?.shineLevel}
                  onChange={(e) => setFormData({ ...formData, shineLevel: parseInt(e?.target?.value) })}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-text-secondary mt-1">
                  <span>Matte</span>
                  <span>Glossy</span>
                </div>
              </div>

              <Input
                label="Durability Rating"
                type="text"
                placeholder="e.g., High traffic areas"
                value={formData?.durability}
                onChange={(e) => setFormData({ ...formData, durability: e?.target?.value })}
                description="Recommended usage scenarios"
              />

              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" variant="default" className="flex-1">
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinishesTab;